<template>
  <b-col lg="6" class="mb-4">
    <WidgetWrapperV2 :title="$t('delivery.dashboard.DeliveryTimeAccuracy')">
      <template #afterTitle>
        <div class="d-inline-flex gap-3 justify-content-end w-100 ontime-delivery-filter">
          <div class="vendor-filter" v-if="hasVmsAccess">
            <multiselect
              v-model="vendorFilter"
              :options="vendorFilterOptions"
              :multiple="false"
              placeholder="Select one"
              label="label"
            />
          </div>
          <div class="date-filter">
            <FormulateInput
              type="date-picker"
              v-model="period"
              format="DD MMM YYYY"
              value-type="YYYY-MM-DDTHH:mm:ssZ"
              range-separator=" - "
              range
              class="p-0 h-auto mb-4"
              :placeholder="$t('global.action.selectDate')"
              :lang="datePickerLang"
              :confirm="true"
              :draft="true"
              :cancel="true"
              :confirmText="$t('global.apply')"
              :shortcuts="datepickerShorcuts"
              @input="handleChangePeriod($event)"
              ></FormulateInput>
          </div>
        </div>
      </template>
      <template #subTitle>
        <p class="mb-0">
          {{ momentDateShortMonth(period?.[0]) }} -
          {{ momentDateShortMonth(period?.[1]) }}
        </p>
      </template>
      <div class="ontime-delivery-pie-chart__wrapper w-100 mt-3">
        <PieChart
          class="ontime-delivery-pie-chart__chart"
          :labels="chartData.labels"
          :datasets="chartData.datasets"
          :options="chartData.options"
          :key="refreshKey"
          :enableDatalabels="true"
        />
        <div class="d-flex justify-content-center gap-4 mt-3">
          <div v-for="(item, index) in chartData.datasets[0]?.count" class="d-flex">
            <span class="ontime-delivery-pie-chart__legend-icon" :style="`background-color: ${chartData.datasets[0]?.backgroundColor[index]};`"></span>
            <div class="d-flex flex-column">
                <span class="ontime-delivery-pie-chart__legend-label">{{ chartData.labels[index] }}</span>
                <span class="ontime-delivery-pie-chart__legend-sublabel">{{ item }}</span>
            </div>
          </div>
        </div>
      </div>
    </WidgetWrapperV2>
    <ModalWidget
      v-if="isOpenModal"
      :achievementDateRange="achievementDateRange"
      :status="statusDO"
      :filters="filters"
      :modalTitle="modalTitle"
      @close="isOpenModal = false"
    />
  </b-col>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment'
import { Helper } from '@/mixin/helper'
import Multiselect from 'vue-multiselect'
import PieChart from '@/components/Chart/Pie'
import datePickerLang from '@/helpers/date-picker-lang'

import { getOnTimeVendorDelivery } from '@/services/rest-api/lib/dashboard/delivery-widget'
import WidgetWrapperV2 from './WidgetWrapperV2.vue'
import { formatEndDateFilter } from '../helpers/formatTime'
import ModalWidget from './ModalWidget.vue'

export default {
  components: {
    PieChart,
    WidgetWrapperV2,
    Multiselect,
    ModalWidget
  },
  mixins: [Helper],
  data() {
    return {
      datasets: [],
      refreshKey: 0,
      vendorFilter: { key: 'all_delivery', label: this.$t('delivery.dashboard.allDelivery') },
      period: [moment().subtract(6, 'd').format("YYYY-MM-DDTHH:mm:ssZ"), moment().format("YYYY-MM-DDTHH:mm:ssZ")],
      constantChartOptions: {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
          datalabels: {
            formatter: (value) => {
              return value.toFixed(0) + "%";
            },
            color: '#fff'
          }
        },
        legend: {
          display: false,
        },
      },
      achievementDateRange: [],
      statusDO: 'DELIVERED',
      isOpenModal: false,
      filters: [],
      modalTitle: ''
    }
  },
  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
    }),
    datePickerLang() {
      return datePickerLang()
    },
    vendorFilterOptions() {
      const vmsOptions = this.hasAccessAll(['vms:create', 'vms:read', 'vms:update', 'vms:delete']) ? [
      { key: 'owner_delivery', label: this.$t('delivery.dashboard.selfDelivery') },
        { key: 'vendor_delivery', label: this.$t('delivery.dashboard.vendorDelivery') }
      ] : []

      return [
        { key: 'all_delivery', label: this.$t('delivery.dashboard.allDelivery') },
        ...vmsOptions,
      ]
    },
    chartData() {
      const data = []
      const labels = []
      const backgroundColor = []
      const count = []
      const periodFilter = this.period
      const vendorFilterValue = this.vendorFilter.key
      const lateLabel = this.$t('delivery.dashboard.late')

      this.datasets.forEach(d => {
        let label = this.$t('delivery.dashboard.onTime'), color = "#429677"
        if (d.label == "late") {
          label = this.$t('delivery.dashboard.late')
          color = "#f64a33"
        }

        data.push(d.percentage)
        backgroundColor.push(color)
        labels.push(label)
        count.push(d.delivery_order_count)
      })

      const unitTranslated = this.$t('delivery.dashboard.delivery')
      const self = this
      return {
        labels,
        datasets: [
          {
            data,
            backgroundColor,
            count,
          }
        ],
        options: {
          ...this.constantChartOptions,
          onClick: function(evt, el) {
            const index = el[0]._index
            const label = this.data.labels[index]

            self.modalTitle = self.$t('delivery.dashboard.onTime')
            self.filters = ['order_is_early eq true']
            if (label === lateLabel) {
              self.filters = ['order_is_late eq true']
              self.modalTitle = self.$t('delivery.dashboard.late')
            }

            self.filters.push(`created_on between (${periodFilter[0]} ${formatEndDateFilter(
              periodFilter[1]
            )})`)
            if (vendorFilterValue === 'owner_delivery') {
              self.filters.push('vendor.id is null')
            } else if (vendorFilterValue === 'vendor_delivery') {
              self.filters.push('vendor.id is_not null')
            }

            self.isOpenModal = true
          },
          tooltips: {
            callbacks: {
              label: function(tooltipItem, data) {
                const label = data.labels[tooltipItem.index]
                return `  ${ label }`;
              },
              afterBody: function(tooltipItem, data) {
                const item = tooltipItem[0]
                const percentageValue = data.datasets[item.datasetIndex].data[item.index]
                const doCount = data.datasets[0].count[item.index]

                return `\n${ percentageValue }%\n${ doCount } ${unitTranslated}`;
              }
            }
          }
        }   
      }
    },
    datepickerShorcuts() {
      return [
        {
          text: this.$t('global.time.today'),
          onClick: () => {
            const startDate = new Date()
            const endDate = new Date()

            startDate.setHours(0, 0, 0)
            endDate.setHours(23, 59, 59)

            return [startDate, endDate]
          },
        },
        {
          text: this.$t('global.time.yesterday'),
          onClick: () => {
            const startDate = new Date()
            const endDate = new Date()

            startDate.setDate(startDate.getDate() - 1)
            startDate.setHours(0, 0, 0)

            endDate.setDate(endDate.getDate() - 1)
            endDate.setHours(23, 59, 59)

            return [startDate, endDate]
          },
        },
        {
          text: this.$t('global.time.last7Days'),
          onClick: () => {
            const startDate = new Date()
            const endDate = new Date()

            startDate.setDate(startDate.getDate() - 7)
            startDate.setHours(0, 0, 0)
            endDate.setHours(23, 59, 59)

            return [startDate, endDate]
          },
        },
        {
          text: this.$t('global.time.last30Days'),
          onClick: () => {
            const startDate = new Date()
            const endDate = new Date()

            startDate.setDate(startDate.getDate() - 30)
            startDate.setHours(0, 0, 0)
            endDate.setHours(23, 59, 59)

            return [startDate, endDate]
          },
        },
        {
          text: this.$t('global.time.thisMonth'),
          onClick: () => {
            const startDate = new Date()
            const endDate = new Date()

            startDate.setDate(1)
            startDate.setHours(0, 0, 0)
            endDate.setHours(23, 59, 59)

            return [startDate, endDate]
          },
        },
        {
          text: this.$t('global.time.lastMonth'),
          onClick: () => {
            const startDate = new Date()
            startDate.setTime(startDate.getTime() - 3600 * 1000 * 24 * startDate.getDate())
            startDate.setTime(startDate.getTime() - 3600 * 1000 * 24 * (startDate.getDate() - 1))
            startDate.setHours(0, 0, 0)

            const endDate = new Date()
            endDate.setTime(endDate.getTime() - 3600 * 1000 * 24 * endDate.getDate())
            endDate.setHours(23, 59, 59)

            return [startDate, endDate]
          },
        },
      ].map((shortcut) => ({ ...shortcut, value: shortcut.onClick }))
    },
    hasVmsAccess() {
      return this.hasAccessAll(['vms:create', 'vms:read', 'vms:update', 'vms:delete'])
    }
  },
  watch: {
    vendorFilter: function () {
      this.getData()
    },
  },
  created() {
    this.getData()
  },
  methods: {
    handleChangePeriod() {
      this.getData()
    },
    async getData() {
      this.loading = true
      const params = {
        view: this.vendorFilter.key,
        filters: `created_on between (${this.period[0]} ${formatEndDateFilter(
          this.period[1]
        )})`,
      }
      const res = await getOnTimeVendorDelivery(params)

      if (res.errors) {
        this.$mcToast.error(res.message)
        return
      }

      // label 'on_time' should be the first in this list followed by 'late'.
      this.datasets = res.data.sort((a, b) => b.label.localeCompare(a.label))
      this.loading = false
      this.refreshKey += 1
    }
  }
}

</script>

<style lang="scss">
  .ontime-delivery-filter {
    flex: 1;
    
    .vendor-filter {
      min-width: 180px;
    }

    .date-filter {
      min-width: 180px;
    }
  }

  .ontime-delivery-pie-chart {
    &__chart {
      max-width: 350px;
      max-height: 350px;
      margin: auto;
    }

    &__legend-icon {
      width: 16px;
      height: 16px;
      margin-top: 1px;
      margin-right: 6px;
      border-radius: 2px;
    }

    &__legend-sublabel {
      font-size: 24px;
      font-weight: 600;
    }
  }
</style>
